import { ComponentProps, JSX } from 'react';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'radix-ui';
import { TranslationKey } from '../../utils/i18n.ts';
import { fallbackFontFamilies } from '../../styles/consts.ts';
import { resetButtonStyles } from '../../styles/helpers.ts';

export type MobileTabsProps = {
  tabs: Array<{
    label: TranslationKey;
    content: JSX.Element;
  }>;
  prefix?: string;
} & ComponentProps<typeof Tabs.Root>;

const MobileTabs = ({
  tabs,
  prefix = defPrefix,
  ...props
}: MobileTabsProps) => {
  const { t } = useTranslation();
  return (
    <Tabs.Root defaultValue={prefix + '1'} {...props}>
      <List aria-label="Tabs">
        {tabs.map(({ label }, index) => (
          <Trigger key={label} value={prefix + `${index + 1}`}>
            {t(label)}
          </Trigger>
        ))}
      </List>
      {tabs.map(({ label, content }, index) => (
        <Content key={label} value={prefix + `${index + 1}`}>
          {content}
        </Content>
      ))}
    </Tabs.Root>
  );
};

const radius = 20;

const List = styled(Tabs.List)`
  display: flex;
  border-radius: ${radius}px;
  background-color: ${({ theme }) => theme.getColor('bgScreen')};
  overflow: hidden;
`;

const Trigger = styled(Tabs.Trigger)`
  ${resetButtonStyles};
  flex: 1 1 50%;
  border-radius: ${radius}px;
  padding: 10px 20px;
  font-weight: 700;
  font-family: Inter, ${fallbackFontFamilies};
  text-transform: uppercase;
  transition:
    background-color 0.4s ease,
    color 0.4s ease;

  &[data-state='active'] {
    background-color: ${({ theme }) => theme.getColor('accentSecondary')};
    color: ${({ theme }) => theme.getColor('completelyBlack')};
  }
`;

const Content = styled(Tabs.Content)`
  margin-top: 16px;
`;

const defPrefix = 'tab';

export { MobileTabs };
